// askSmart-ui/src/pages/TOSP/components/LeaseReview/leaseFingerprint.ts

// Whether the document open now is the one that was reviewed before.
//
// Taken from the bytes rather than from a name, a slot key or a timestamp. A
// replacement lease is routinely uploaded into the same SMRT Docs slot under
// the same title, so anything short of the content itself would report a new
// agreement as the old one and carry a sign-off forward onto a document nobody
// has read. The bytes are the only thing that can't lie about that.

/** How many hex characters of the digest to keep. Ample against collision. */
const LENGTH = 32;

const hex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

// FNV-1a. The fallback for a page served over plain http, where SubtleCrypto
// isn't available — weaker than SHA-256, but this only has to change when the
// document does, not resist anyone trying to make two leases collide.
function checksum(bytes: Uint8Array): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i += 1) {
    hash ^= bytes[i];
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `fnv${bytes.length.toString(16)}-${hash.toString(16)}`;
}

export async function fingerprintLease(bytes: Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return checksum(bytes);

  try {
    // Copied, because digest detaches nothing but takes the view's buffer as
    // given — and the caller still needs these bytes to render and extract.
    const digest = await subtle.digest("SHA-256", bytes.slice());
    return hex(digest).slice(0, LENGTH);
  } catch {
    return checksum(bytes);
  }
}
